import React from 'react';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import dayjs from "dayjs";

// Registrar componentes do Chart.js
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

interface ConsumoChartProps {
    consumo: string;
    dataInicial: string;
}

const ConsumoChart: React.FC<ConsumoChartProps> = ({ consumo, dataInicial }) => {
    // Nomes dos meses em português (abreviados)
    const mesesNomes = [
        'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
        'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
    ];

    // Parse do consumo: "200,250,400" -> [200, 250, 400]
    let consumoValues = consumo
        ? consumo.split(',').map(v => parseFloat(v.trim())).filter(v => !isNaN(v))
        : [];

    // Parse da data inicial
    let startDate = dayjs();
    if (dataInicial) {
        // Tentar parsear a data (pode vir como string "MM/YYYY" ou como Date)
        const dataStr = typeof dataInicial === 'string' ? dataInicial : dayjs(dataInicial).format('MM/YYYY');
        const parts = dataStr.split('/');
        if (parts.length === 2) {
            // MM/YYYY
            const month = parseInt(parts[0]) - 1;
            const year = parseInt(parts[1]);
            startDate = dayjs().year(year).month(month).date(1);
        } else if (parts.length === 3) {
            // DD/MM/YYYY
            const month = parseInt(parts[1]) - 1;
            const year = parseInt(parts[2]);
            startDate = dayjs().year(year).month(month).date(1);
        } else if (dataStr.includes('-')) {
            // YYYY-MM-DD
            const d = dayjs(dataStr);
            if (d.isValid()) startDate = d;
        }
    }

    // Agrupar valores por mês (0-11 = Janeiro a Dezembro) respeitando a data inicial
    // O primeiro valor (índice 0) corresponde à data inicial
    // Os demais são retroativos (índice 1 = data inicial - 1 mês, etc.)
    const monthGroups: number[][] = Array.from({ length: 12 }, () => []);

    consumoValues.forEach((value, index) => {
        // Calcula a data deste consumo (retroativo à data inicial)
        const consumoDate = startDate.subtract(index, 'month');
        const monthIndex = consumoDate.month(); // 0-11 (Jan=0, Dez=11)
        monthGroups[monthIndex].push(value);
    });

    // Calcular a média de cada grupo de mês
    const consumosPorMes: number[] = [];

    for (let mes = 0; mes < 12; mes++) {
        if (monthGroups[mes].length > 0) {
            const sum = monthGroups[mes].reduce((a, b) => a + b, 0);
            const media = sum / monthGroups[mes].length;
            consumosPorMes[mes] = Math.round(media);
        } else {
            consumosPorMes[mes] = 0;
        }
    }

    // Gera labels e dados sempre de Janeiro a Dezembro
    const labels: string[] = mesesNomes;
    const dataPoints: number[] = consumosPorMes;

    const data = {
        labels,
        datasets: [
            {
                label: 'Consumo (kWh)',
                data: dataPoints,
                borderColor: '#008FFB',
                backgroundColor: 'rgba(0, 143, 251, 0.1)',
                borderWidth: 2,
                tension: 0.4,
                pointRadius: 2,
                pointHoverRadius: 4,
                fill: true
            }
        ]
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                enabled: true,
                callbacks: {
                    label: function(context: any) {
                        return `${context.parsed.y} kWh`;
                    }
                }
            }
        },
        scales: {
            x: {
                display: true,
                grid: {
                    display: false
                },
                ticks: {
                    font: {
                        size: 9
                    },
                    maxRotation: 0,
                    autoSkip: true,
                    maxTicksLimit: 6
                }
            },
            y: {
                display: false,
                grid: {
                    display: false
                }
            }
        },
        layout: {
            padding: 0
        }
    };

    return (
        <div style={{
            height: '73px',
            width: '100%',
            position: 'relative',
            background: '#fff',
            border: '1px solid #EEE',
            borderRadius: '4px',
            padding: '5px'
        }}>
            <Line data={data} options={options} />
        </div>
    );
};

export default ConsumoChart;
